import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(" ") : "Checkout failed";
      toast.error(msg);
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="nx-container py-24 text-center">
        <span className="mono text-[11px] text-slate-500">EMPTY/CART</span>
        <h1 className="text-4xl font-black tracking-tighter mt-2 mb-4">Your cart awaits.</h1>
        <p className="text-slate-600 mb-8">Discover 10M+ products across retail and wholesale.</p>
        <Link to="/products" data-testid="continue-shopping-btn" className="nx-btn-primary inline-block">Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="nx-container py-10">
      <span className="mono text-[11px] text-slate-500">01/CART</span>
      <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-1 mb-8">Your cart ({items.length})</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        <div className="border border-slate-200 bg-white divide-y divide-slate-200">
          {items.map(i => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="p-4 flex gap-4 items-center">
              <img src={i.image} alt={i.name} className="w-20 h-20 object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-bold line-clamp-2">{i.name}</p>
                <p className="mono text-[11px] text-slate-500 mt-1">${i.price.toFixed(2)}/UNIT</p>
              </div>
              <div className="flex items-center gap-2 border border-slate-900">
                <button onClick={() => updateQty(i.product_id, i.quantity - 1)} data-testid={`dec-${i.product_id}`} className="px-2 py-1 hover:bg-slate-100">-</button>
                <span className="w-8 text-center text-sm">{i.quantity}</span>
                <button onClick={() => updateQty(i.product_id, i.quantity + 1)} data-testid={`inc-${i.product_id}`} className="px-2 py-1 hover:bg-slate-100">+</button>
              </div>
              <div className="w-24 text-right font-black">${(i.price * i.quantity).toFixed(2)}</div>
              <button onClick={() => removeItem(i.product_id)} data-testid={`remove-${i.product_id}`} className="text-slate-400 hover:text-red-600 p-2" style={{transition:"color 0.2s ease"}}>
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        <aside className="border border-slate-900 p-6 bg-white h-fit">
          <h3 className="mono text-[11px] text-slate-500 mb-4">ORDER/SUMMARY</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="text-emerald-700 font-bold">FREE</span></div>
            <div className="flex justify-between"><span>Estimated Tax</span><span>Calculated at next step</span></div>
          </div>
          <div className="border-t border-slate-900 mt-4 pt-4 flex justify-between items-baseline">
            <span className="mono text-[11px]">TOTAL</span>
            <span className="text-3xl font-black">${subtotal.toFixed(2)}</span>
          </div>
          <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="mt-6 w-full nx-btn-accent disabled:opacity-50">
            {loading ? "Redirecting..." : "Proceed to Checkout →"}
          </button>
          <p className="mono text-[10px] text-slate-500 mt-4 text-center">SECURED/BY/STRIPE</p>
        </aside>
      </div>
    </div>
  );
}
